import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ESOganisationDropdown from "../components/dropdown/esOrganisationDropdown";
import SectionHeader from "../components/section/SectionHeader";
import {
  ADD_DFSI_CONNECTION_FORM_FIELDS,
  DFSI_CONNECTIONS_FILTER_COLUMNS,
  DFSI_CONNECTIONS_TABLE_HEADERS,
  EDIT_DFSI_CONNECTION_FORM_FIELDS,
} from "../constants";
import AuthContext from "../store/authContext";
import {
  createSearchConfig,
  errorAlert,
  makeApiRequest,
  successAlert,
  transformDFSIConnectionResponseRow,
  transformDFSIConnectionUpdateBody,
  transformDFSIConnectionsRows,
} from "../util";
import DataTableWithControls from "../components/dataTable/DataTableWithControls";
import OrgContext from "../store/orgContext";
import InfoAlert from "../ui/InfoAlert";
import TableActionButton from "../ui/TableActionButton";
import AdvancedSearch from "../components/advancedSearch/AdvancedSearch";
import {
  deleteDFSIConnection,
  getDFSIConnectionByRef,
  updateDFSIConnectionByRef,
} from "../api";
import { INITIAL_DFSI_CONNECTION_EDIT_STATE } from "../constants/editState";
import EditDFSIConnectionRow from "../components/dataTable/EditDFSIConnectionRow";
import useHideSidePanel from "../hooks/useHideSidePanel";
import {
  READ_DFSI_CONNECTION_TRANSLATIONS,
  DELETE_DFSI_CONNECTION_TRANSLATIONS,
} from "../translations/util";
import useAppTitle from "../hooks/useAppTitle";
import SearchContext from "../store/searchContext";
import BulkUpload from "../components/bulkUpload/BulkUpload";

interface ISearchData {
  "CONNECTION-ID"?: string | null;
}

const INITIAL_STATE = {
  "CONNECTION-ID": "",
};

const searchFields = ["CONNECTION-ID"];

const searchConfigNew = createSearchConfig(
  searchFields,
  ADD_DFSI_CONNECTION_FORM_FIELDS
);

const getAlertHeading = ({
  isDataSearched,
  connectionsData,
}: {
  isDataSearched: boolean | null | string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  connectionsData: Array<any>;
}) => {
  if (!connectionsData.length) {
    if (isDataSearched) {
      return READ_DFSI_CONNECTION_TRANSLATIONS.infoAlertHeading.dataSearched;
    }
    return READ_DFSI_CONNECTION_TRANSLATIONS.infoAlertHeading.dataNotSearched;
  }
  return undefined;
};

const getAlertSubText = ({
  isDataSearched,
  connectionsData,
}: {
  isDataSearched: boolean | null | string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  connectionsData: Array<any>;
}) => {
  if (!connectionsData.length) {
    if (isDataSearched) {
      return READ_DFSI_CONNECTION_TRANSLATIONS.infoAlertSubText.dataSearched;
    }
    return READ_DFSI_CONNECTION_TRANSLATIONS.infoAlertSubText.dataNotSearched;
  }
  return undefined;
};

const DFSIConnections = () => {
  const {
    token,
    scopes: { dfsi_connections: dfsiConnections },
  } = useContext(AuthContext);
  const { orgs: orgList, selectedOrg, updateOrg } = useContext(OrgContext);
  const { selectedColumn, setSelectedColumn, setDisableSearch, setSearchText } =
    useContext(SearchContext);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [connectionsData, setConnectionsData] = useState<any[]>([]);

  // State to check if Bulk Upload is Clicked
  const [isBulkUploadEnabled, setIsBulkUploadEnabled] = useState(false);
  const {
    showSidePanel,
    showAdvancedSearch,
    handleToggleAdvanceSearchPanel,
    handleToggleSidePanel,
  } = useHideSidePanel();
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);
  useAppTitle("DFSI Connections- IWF Config Admin Tool (ICAT) by AT&T");
  const [searchData, setSearchData] = useState<ISearchData>(INITIAL_STATE);
  const [numberOfRows, setNumberOfRows] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [searchQuery, setSearhQuery] = useState<{
    data: ISearchData;
    pageNum: number;
    pageSize: number;
    sortByField?: string;
    sortDirection?: "desc" | "asc";
  }>({
    pageSize: 20,
    pageNum: 1,
    data: {},
    sortByField: undefined,
    sortDirection: undefined,
  });

  const fetchDFSIConnections = async () => {
    setLoading(true);
    try {
      if (selectedOrg) {
        const { data, pageNum, pageSize, sortByField, sortDirection } =
          searchQuery;
        const queryParams = new URLSearchParams({
          page: `${pageNum}`,
          pageSize: `${pageSize}`,
          ...(sortByField && { sortByField }),
          ...(sortDirection && { sortDirection }),
          ...(data &&
            data["CONNECTION-ID"] && {
              "CONNECTION-ID": data["CONNECTION-ID"],
            }),
        });
        const response = await makeApiRequest<{
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          Rows: Array<any>;
          NbrOfRows: number;
          NbrOfPages: number;
        }>({
          path: `/api/v1/app-config/dfsi-connections?${queryParams}`,
          token,
          org: selectedOrg.id,
        });
        setNumberOfRows(response.NbrOfRows);
        setNumberOfPages(response.NbrOfPages);
        setConnectionsData(response.Rows);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  const deleteConnection = async (ref: string) => {
    if (selectedOrg && token) {
      try {
        await deleteDFSIConnection({
          token,
          ref,
          org: selectedOrg.id,
        });
        successAlert(DELETE_DFSI_CONNECTION_TRANSLATIONS.successAlertMessage);
        handleToggleSidePanel();
        setSelectedConnectionId(null);
        fetchDFSIConnections();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      }
    }
  };

  const sortTable = (col: string, direction: "asc" | "desc") => () => {
    setSearhQuery({
      ...searchQuery,
      sortByField: col,
      sortDirection: direction,
    });
  };

  useEffect(() => {
    setSelectedColumn(null);
    setSearchText("");
    setDisableSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchData(INITIAL_STATE);
    setSearhQuery({
      pageSize: 20,
      pageNum: 1,
      data: {},
      sortByField: undefined,
      sortDirection: undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg]);

  useEffect(() => {
    fetchDFSIConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, searchQuery]);

  useEffect(() => {
    if (!showAdvancedSearch && Object.keys(searchQuery.data).length === 0) {
      setSearchData(INITIAL_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdvancedSearch]);

  return (
    <>
      <SectionHeader
        pageTitle={READ_DFSI_CONNECTION_TRANSLATIONS.header}
        pageDescription={READ_DFSI_CONNECTION_TRANSLATIONS.headerSubtext}
        sideComponent={
          <div className="dropdown-details">
            <ESOganisationDropdown
              org={selectedOrg || undefined}
              orgsList={orgList}
              setOrg={updateOrg}
            />
          </div>
        }
      />

      {selectedOrg ? (
        <>
          {showAdvancedSearch ? (
            <AdvancedSearch
              setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
              advancedSearchState={searchData}
              advancedSearchConfig={searchConfigNew}
              setAdvancedSearchState={setSearchData}
              onSubmit={(data) =>
                setSearhQuery({
                  ...searchQuery,
                  pageNum: 1,
                  pageSize: 20,
                  data,
                })
              }
              onCancel={() => {
                setSearchData(INITIAL_STATE);
                handleToggleAdvanceSearchPanel();
              }}
            />
          ) : null}

          {showSidePanel && isBulkUploadEnabled ? (
            <BulkUpload
              moduleName="DFSI Connection"
              pathName="/api/v1/app-config/dfsi-connections"
              csvTemplateFile={[
                "CONNECTION-ID,IWF-ROLE,DFSI-VERSION,HOST-CONTROL-IPV4-ADDR,HOST-CONTROL-PORT,HOST-VOICE-IPV4-ADDR,VOICE-PORT,DATA-IPV4-ADDR,DATA-PORT,FIXED-STATION-CONTROL-IPV4-ADDR,FIXED-STATION-VOICE-IPV4-ADDR,FIXED-STATION-CONTROL-PORT,CONNECTIVITY-TX-TIMER,CONNECTIVITY-LOSS-LIMIT,FS-HEARTBEAT-PERIOD,HOST-HEARTBEAT-PERIOD,CONTROL-RETRY-TIME,CONTROL-ATTEMPT-LIMIT,OUTBOUND-MAX-CAI-DATA-BLOCKS,INBOUND-MAX-CAI-DATA-BLOCKS,ACKNOWLEDGEMENT-DELAY,INBOUND-REPEAT,FIXED-STATION-VOTER-REPORTING-PERIOD,FIXED-STATION-TRANSMITTER-REPORT-INTERVAL",
              ]}
              closeBulkUploadSidePanel={() => {
                handleToggleSidePanel();
                setIsBulkUploadEnabled(false);
                fetchDFSIConnections();
              }}
            />
          ) : null}

          {showSidePanel &&
          selectedOrg &&
          selectedConnectionId &&
          !isBulkUploadEnabled ? (
            <EditDFSIConnectionRow
              panelName={READ_DFSI_CONNECTION_TRANSLATIONS.editRow.panelName}
              initialState={INITIAL_DFSI_CONNECTION_EDIT_STATE}
              org={selectedOrg.id}
              esRef={selectedConnectionId}
              setShowEditPanel={handleToggleSidePanel}
              onDelete={() => deleteConnection(selectedConnectionId)}
              formFields={EDIT_DFSI_CONNECTION_FORM_FIELDS}
              header={READ_DFSI_CONNECTION_TRANSLATIONS.editRow.header}
              transformResponseRow={transformDFSIConnectionResponseRow}
              transformUpdateBody={transformDFSIConnectionUpdateBody}
              onUpdate={updateDFSIConnectionByRef}
              loadDataByRef={getDFSIConnectionByRef}
              hasDeletePermission={
                dfsiConnections && dfsiConnections.includes("d")
              }
              hasEditPermission={
                dfsiConnections && dfsiConnections.includes("u")
              }
              refreshTable={fetchDFSIConnections}
              deleteModalItemName="CONNECTION-ID"
            />
          ) : null}

          <DataTableWithControls
            loading={loading}
            showAdvancdSearch={false}
            filterColumns={DFSI_CONNECTIONS_FILTER_COLUMNS}
            selectedColumn={selectedColumn}
            setSelectedColumn={setSelectedColumn}
            onSearch={(term) =>
              setSearhQuery({
                ...searchQuery,
                pageNum: 1,
                pageSize: 20,
                data: { [`${selectedColumn?.id}`]: term },
              })
            }
            setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
            showCreateButton={dfsiConnections && dfsiConnections.includes("c")}
            onCreateButtonClick={() => {
              navigate(`/configurationManagement/dfsiConnections/new`);
            }}
            rows={transformDFSIConnectionsRows(connectionsData).map(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (row: any) => ({
                ...row,
                action: (
                  <TableActionButton
                    onActionButtonClick={() => {
                      setSelectedConnectionId(row.id);
                      handleToggleSidePanel();
                    }}
                  />
                ),
              })
            )}
            infoAlertHeading={getAlertHeading({
              isDataSearched:
                selectedColumn && selectedColumn.id in searchQuery.data,
              connectionsData,
            })}
            infoAlertSubtext={getAlertSubText({
              isDataSearched:
                selectedColumn && selectedColumn.id in searchQuery.data,
              connectionsData,
            })}
            numberOfAdvancedSearchFilters={searchConfigNew.reduce(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (acc, el: any) => {
                const { name } = el;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const obj: any = { ...searchData };
                return acc + (obj[name] && obj[name].length > 0 ? 1 : 0);
              },
              0
            )}
            clearAdvancedSearchFilters={() => {
              setSearchData(INITIAL_STATE);
              setSearhQuery({
                ...searchQuery,
                pageNum: 1,
                pageSize: 20,
                data: {},
              });
            }}
            headers={DFSI_CONNECTIONS_TABLE_HEADERS}
            pageNum={searchQuery.pageNum}
            setPageNum={(pageNum) => setSearhQuery({ ...searchQuery, pageNum })}
            totalItems={numberOfRows}
            totalPages={numberOfPages}
            pageSize={searchQuery.pageSize}
            setPageSize={(pageSize) =>
              setSearhQuery({ ...searchQuery, pageSize })
            }
            sortByField={searchQuery.sortByField}
            sortDirection={searchQuery.sortDirection}
            handleSort={sortTable}
            showBulkUploadButton={
              dfsiConnections && dfsiConnections.includes("c")
            }
            onBulkUploadButtonClick={() => {
              handleToggleSidePanel();
              setIsBulkUploadEnabled(true);
            }}
          />
        </>
      ) : (
        <InfoAlert
          heading={READ_DFSI_CONNECTION_TRANSLATIONS.infoAlert.heading}
          subText={READ_DFSI_CONNECTION_TRANSLATIONS.infoAlert.subText}
        />
      )}
    </>
  );
};

export default DFSIConnections;
